import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '../../../../../ns-elements/ns-sign-button.js';

import { ltnAuthServiceContext, ltnDialogServiceContext } from '../../../../../ns-services/contexts.js';
import { LtnAuthService } from '../../../../../ltn-auth/ltn-auth-service.js';
import { LtnDialogService } from '../../../../../ltn-dialog/ltn-dialog-service.js';
import { nsSharedStyles } from '../../../../../ns-styles/index.js';
import { callLambda } from '../../../../../helpers/lambda.js';
import { collectHMRCDeviceInfo } from '../../../../../helpers/fraud-prevention.js';
import { hmrcSandboxBodyFields, readHmrcSandbox } from '../../../../../helpers/hmrc-sandbox.js';
import { rememberOAuthStart } from '../../../../../views/oauth/oauth-landing.js';

import {
  INITIAL_SCORE_STATE,
  WORDING,
  canComplete,
  isScoreProblem,
  reduce,
  scoreAction,
  type HmrcCardKind,
  type ScoreAnswer,
  type ScoreEvent,
  type ScoreState,
} from './trust-score-logic.js';

export type NoVatAnswer = 'NOT_REGISTERED' | 'NOT_MTD';

export interface TrustScoreRequest {
  jobId: string;
  companyId: string;
  noVat?: NoVatAnswer;
  appMode?: string | null;
}

/** POSTs hmrc/trust-score. The lambda answers HTTP 200 with its own code and status, which the caller reads. */
export async function postTrustScore(token: string | undefined, request: TrustScoreRequest): Promise<ScoreAnswer> {
  const body = {
    jobId: request.jobId,
    companyId: request.companyId,
    deviceInfo: collectHMRCDeviceInfo(),
    ...(request.noVat ? { noVat: request.noVat } : hmrcSandboxBodyFields(readHmrcSandbox(), request.appMode)),
  };
  const response = await callLambda('hmrc/trust-score', 'POST', token, JSON.stringify(body));
  return response.res as unknown as ScoreAnswer;
}

/** Asks before storing "not signed up for MTD", which zeroes HMRC and removes the company's HMRC connection. */
export async function confirmNotMtd(dialog: LtnDialogService | undefined): Promise<boolean> {
  if (!dialog) return window.confirm(`${WORDING.notMtdConfirmTitle}\n\n${WORDING.notMtdConfirm}`);
  try {
    await dialog.open(
      {
        title: WORDING.notMtdConfirmTitle,
        description: WORDING.notMtdConfirm,
        actionLabel: WORDING.notMtdConfirmAction,
      },
      { sections: [] },
    );
    return true;
  } catch {
    // Cancel, Escape, the close button or the backdrop
    return false;
  }
}

/**
 * The HMRC trust-score step of the validation card (after Next) and the review card: the consent text, the running
 * state, the answer, and for RECONNECT the two possibilities with Reconnect and "Not signed up for MTD". Run and Retry
 * belong in the card's button bar, so the card draws them with `renderScoreBar()` and listens for `score-state-change`.
 * While a run is out the card's own loader covers it. Once a run has answered, whatever it said, the bar offers Complete:
 * the user completes the job (the lambda never does), with or without an HMRC score. `score-complete` still marks OK.
 * Once the job is complete the card sets `completed`, which keeps the answer on show without the prompts to complete.
 */
@customElement('ns-hmrc-trust-score-step')
export class NSHmrcTrustScoreStep extends LitElement {
  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  @consume({ context: ltnDialogServiceContext })
  private __dialog?: LtnDialogService;

  @property({ type: String })
  kind: HmrcCardKind = 'validation';

  @property({ attribute: false })
  jobId = '';

  @property({ attribute: false })
  companyId = '';

  // The VRN HMRC proved, which a reconnect signs in for, and the HMRC service the entry was made against
  @property({ attribute: false })
  provenId: string | null = null;

  @property({ attribute: false })
  environment: string | null = null;

  @property({ attribute: false })
  appMode: string | null = null;

  // HMRC answered "not authorised" at the last sign-in (HMRC_NOT_MTD on the job): start at the two possibilities
  @property({ type: Boolean, attribute: 'not-mtd-recorded' })
  notMtdRecorded = false;

  // The job is complete: the answer stays, the prompts to complete it go
  @property({ type: Boolean })
  completed = false;

  @state()
  private __state: ScoreState = INITIAL_SCORE_STATE;

  @state()
  private __busy = false;

  private __timer?: ReturnType<typeof setInterval>;

  static styles = [
    nsSharedStyles,
    css`
      :host {
        display: block;
        padding: var(--wa-space-l);
        border-radius: var(--ns-card-border-radius, 0.5em);
        background: var(--wa-color-surface-lowered);
      }

      :host > * {
        margin: 0;
      }

      :host > * + * {
        margin-top: var(--wa-space-m);
      }

      h3 {
        font-size: var(--wa-font-size-l);
        font-weight: 500;
      }

      p {
        font-weight: 300;
      }

      .result {
        font-weight: 500;
      }

      .options {
        display: flex;
        flex-wrap: wrap;
        gap: var(--wa-space-s);
      }
    `,
  ];

  get scoreState(): ScoreState {
    return this.__state;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.notMtdRecorded && this.__state.phase === 'idle') {
      this.__state = { phase: 'reconnect', message: null, secondsLeft: 0 };
    }
    if (this.__state.phase === 'countdown') this.__startTimer();
  }

  // HMRC's not-MTD answer can land after the step is drawn (the card reads its job late, or a realtime update)
  protected willUpdate(changed: PropertyValues<this>) {
    if (changed.has('notMtdRecorded') && this.notMtdRecorded && this.__state.phase === 'idle') {
      this.__state = { phase: 'reconnect', message: null, secondsLeft: 0 };
      this.dispatchEvent(
        new CustomEvent('score-state-change', { detail: this.__state, bubbles: true, composed: true }),
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.__stopTimer();
    if (this.__state.phase === 'running') this.__loading(false);
  }

  // The card's loader (the job container's overlay) covers the card while a run is out
  private __loading(on: boolean) {
    this.dispatchEvent(
      on
        ? new CustomEvent('job-card-loading', { detail: WORDING.running, bubbles: true, composed: true })
        : new CustomEvent('job-card-loading-end', { bubbles: true, composed: true }),
    );
  }

  // The card draws Run in its bar from this state, so it hears of the first one too
  protected firstUpdated() {
    this.dispatchEvent(new CustomEvent('score-state-change', { detail: this.__state, bubbles: true, composed: true }));
  }

  private __apply(event: ScoreEvent) {
    const next = reduce(this.__state, event);
    if (next === this.__state) return;
    const wasRunning = this.__state.phase === 'running';
    this.__state = next;
    if (next.phase === 'running' && !wasRunning) this.__loading(true);
    if (next.phase !== 'running' && wasRunning) this.__loading(false);
    if (next.phase === 'countdown') this.__startTimer();
    else this.__stopTimer();
    this.dispatchEvent(new CustomEvent('score-state-change', { detail: next, bubbles: true, composed: true }));
    if (next.phase === 'done') {
      this.dispatchEvent(new CustomEvent('score-complete', { detail: next, bubbles: true, composed: true }));
    }
  }

  private __startTimer() {
    if (this.__timer) return;
    this.__timer = setInterval(() => this.__apply({ type: 'tick' }), 1000);
  }

  private __stopTimer() {
    if (!this.__timer) return;
    clearInterval(this.__timer);
    this.__timer = undefined;
  }

  /** Runs the score (Run or Retry); does nothing while a run is in progress or counting down. */
  async run(noVat?: NoVatAnswer) {
    if (this.__busy) return;
    const before = this.__state;
    this.__apply({ type: 'run' });
    if (this.__state === before) return;
    this.__busy = true;
    try {
      const answer = await postTrustScore(this.__auth?.token, {
        jobId: this.jobId,
        companyId: this.companyId,
        noVat,
        appMode: this.appMode,
      });
      this.__apply({ type: 'answer', answer, declared: noVat === 'NOT_MTD' });
    } catch (err) {
      this.__apply({
        type: 'failed',
        message: err instanceof Error ? err.message : null,
        declared: noVat === 'NOT_MTD',
      });
    } finally {
      this.__busy = false;
    }
  }

  private async __reconnect() {
    if (this.__busy) return;
    if (!this.provenId) {
      this.__state = { ...this.__state, message: 'The VAT number HMRC confirmed for this company could not be found.' };
      return;
    }
    this.__busy = true;
    try {
      const body = {
        jobId: this.jobId,
        VRN: this.provenId,
        // The review card's sign-in always uses the entry's own service; the validation card's names it
        ...(this.kind === 'validation' && this.environment ? { selectedEnv: this.environment } : {}),
        deviceInfo: collectHMRCDeviceInfo(),
      };
      const response = await callLambda('hmrc/auth', 'POST', this.__auth?.token, JSON.stringify(body));
      const { url } = response.res as unknown as { url?: unknown };
      if (typeof url !== 'string' || !url) throw new Error('Unable to start HMRC sign-in');
      // A declined sign-in comes back to this card (view-oauth)
      rememberOAuthStart('hmrc', this.jobId);
      window.location.href = url;
    } catch (err) {
      this.__busy = false;
      this.__state = { ...this.__state, message: err instanceof Error ? err.message : 'Unable to start HMRC sign-in' };
    }
  }

  private async __notMtd() {
    if (this.__busy) return;
    if (!(await confirmNotMtd(this.__dialog))) return;
    await this.run('NOT_MTD');
  }

  // After a failure: the job can still be completed without an HMRC score
  private __renderCompleteWithout() {
    if (this.completed) return nothing;
    return html`
      <p>${this.kind === 'review' ? WORDING.completeWithoutReview : WORDING.completeWithout}</p>
    `;
  }

  private __renderResult() {
    const { phase, message } = this.__state;
    // The card's loader covers the card while a run is out
    if (phase === 'running') return nothing;
    if (phase === 'reconnect') {
      return html`
        <h3>${WORDING.reconnectHeading}</h3>
        ${message
          ? html`
              <p class="result" role="alert">${message}</p>
            `
          : nothing}
        <p>${WORDING.reconnectOptions}</p>
        <div class="options">
          <wa-button variant="brand" ?disabled=${this.__busy} @click=${() => this.__reconnect()}>Reconnect</wa-button>
          <wa-button variant="brand" appearance="outlined" ?disabled=${this.__busy} @click=${() => this.__notMtd()}>
            Not signed up for MTD
          </wa-button>
        </div>
        ${this.__renderCompleteWithout()}
      `;
    }
    if (!message) return nothing;
    if (phase === 'done') {
      return html`
        <p class="result" role="status">${message}</p>
        ${this.completed
          ? nothing
          : html`
              <p>${WORDING.doneNext}</p>
            `}
      `;
    }
    return html`
      <p class="result" role=${isScoreProblem(this.__state) ? 'alert' : 'status'}>${message}</p>
      ${isScoreProblem(this.__state) ? this.__renderCompleteWithout() : nothing}
    `;
  }

  render() {
    if (this.__state.phase === 'reconnect') return this.__renderResult();
    return html`
      <h3>${WORDING.consentHeading}</h3>
      <p>${WORDING.consent}</p>
      ${this.__renderResult()}
    `;
  }
}

/** The info panel beside the score step: what the score reads from HMRC (the validation card after Next, the review card). */
export function renderScoreInfo() {
  return html`
    <div slot="aside" class="primary-info">
      <span class="info-icon" aria-hidden="true">i</span>
      <h3>What the score reads</h3>
      <ul class="ticks">
        <li>Your VAT returns and when they were filed</li>
        <li>Any VAT owed and how overdue it is</li>
        <li>Penalties and penalty points</li>
        <li>Your VAT registration</li>
      </ul>
      <p class="note">Read-only: nothing is changed at HMRC.</p>
    </div>
  `;
}

export interface ScoreBarOptions {
  // Disables the completing button while the card's completion is out
  completing?: boolean;
  // Completes with the public ledger signing button, described by this text, in place of the plain Complete
  sign?: string | null;
}

/**
 * The score step's buttons for the card's bar: Run before a run; once one has answered, Complete, with Retry beside it
 * (counting down while HMRC is busy) when the run can be tried again. A plain Complete is the outlined one of the pair and
 * comes first, at the far left, with Retry filled at the far right. With `sign`, the public ledger signing button completes
 * the job instead: it is the filled, final action at the far right, so an outlined Retry comes first.
 * @param complete the card's Complete (its job's outcome)
 */
export function renderScoreBar(
  step: NSHmrcTrustScoreStep | null | undefined,
  complete: () => unknown,
  { completing = false, sign = null }: ScoreBarOptions = {},
) {
  if (!step) return nothing;
  const action = scoreAction(step.scoreState);
  const answered = canComplete(step.scoreState);
  const retry = action
    ? html`
        <wa-button
          variant="brand"
          appearance=${answered && sign ? 'outlined' : 'accent'}
          ?disabled=${action.disabled}
          @click=${() => step.run()}
        >
          ${action.seconds > 0 ? `${action.label} in ${action.seconds}s` : action.label}
        </wa-button>
      `
    : nothing;
  if (!answered) return retry;
  if (sign) {
    return html`
      ${retry}
      <ns-sign-button description=${sign} ?disabled=${completing} @click=${() => complete()}></ns-sign-button>
    `;
  }
  return html`
    <wa-button
      variant="brand"
      appearance=${action ? 'outlined' : 'accent'}
      ?disabled=${completing}
      @click=${() => complete()}
    >
      Complete
    </wa-button>
    ${retry}
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-hmrc-trust-score-step': NSHmrcTrustScoreStep;
  }
}
